import { useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ApiError, backendCode } from '@/api/errors';
import { commissionPlanErrorMessage } from '@/features/commissionPlans/commissionPlanErrors';
import { ActionErrorAlert } from './ActionErrorAlert';
import { SubmitButton } from './SubmitButton';
import { useActionError, type ActionError } from './useActionError';
import { useFieldErrors } from './useFieldErrors';
import { useToast } from './useToast';

const FIELDS = ['name'] as const;

/** Mounted for one rename; the terms editor never contributes to this request. */
export function LiveRenameDialog({
  name: initialName,
  onSave,
  onClose,
  onReload,
}: {
  name: string;
  onSave: (name: string) => Promise<unknown>;
  onClose: () => void;
  onReload: () => void;
}) {
  const { t } = useTranslation();
  const describe = useActionError(commissionPlanErrorMessage);
  const toast = useToast();
  const [name, setName] = useState(initialName);
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const input = useRef<HTMLInputElement>(null);
  const [conflict, setConflict] = useState(false);
  const [error, setError] = useState<Pick<ActionError, 'message' | 'requestId'> | null>(null);
  const fields = useFieldErrors(FIELDS, { name: input }, busy);
  const prefix = 'COMMISSION_PLAN';
  const keys = 'commissionPlans';
  async function save() {
    if (pending.current) return;
    if (!name.trim()) {
      setError(null);
      fields.fail({ name: t('validation.name') });
      return;
    }
    pending.current = true;
    setBusy(true);
    fields.reset();
    setConflict(false);
    setError(null);
    try {
      await onSave(name.trim());
      toast(t('common.renamed'));
      onClose();
      onReload();
    } catch (err) {
      if (backendCode(err) === `${prefix}_NAME_TAKEN`) {
        fields.fail({
          name: t('commissionPlans.error.nameTaken'),
        });
      } else if (backendCode(err) === `${prefix}_VERSION_CONFLICT`) {
        setConflict(true);
        setError({
          message: t(`${keys}.error.versionConflict`),
          requestId: err instanceof ApiError ? err.requestId : undefined,
        });
      } else setError(describe(err));
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <Box
        component="form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <DialogTitle>{t(`${keys}.rename.title`)}</DialogTitle>
        <DialogContent>
          <ActionErrorAlert
            error={error}
            action={
              conflict ? (
                <Button
                  color="inherit"
                  onClick={() => {
                    onClose();
                    onReload();
                  }}
                >
                  {t('common.reload')}
                </Button>
              ) : undefined
            }
          />
          <TextField
            required
            fullWidth
            margin="dense"
            inputRef={input}
            label={t(`${keys}.rename.name`)}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              fields.clear('name');
            }}
            error={fields.errors.name !== undefined}
            helperText={fields.errors.name}
            slotProps={{ htmlInput: { maxLength: 80, readOnly: busy } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <SubmitButton type="submit" loading={busy}>
            {t('common.save')}
          </SubmitButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
